import React, { useEffect, useMemo } from 'react';

import Select from 'react-select';

import * as Service from '../../../services/VendorsService';
import {
  BookingPlatformSupplier,
  IntegrationsVendorTypes,
  mapVendorTypeToOption,
  Vendor,
  VendorType,
  VendorTypeOptions,
} from '../../../types/Vendor';
import FieldView from '../../utils/FieldView';
import { SelectOption } from '../../../infrastructure/utils/common';
import { useFetchData } from '../../../infrastructure/hooks/useFetchData';
import { ApiStatus } from '../../../infrastructure/constants/apiStatus';

type Props = {
  onSave(fields: Pick<Vendor, 'bookingPlatforms' | 'type'>): void;
} & Pick<Vendor, 'bookingPlatforms' | 'type'>;

const BookingPlatformSelector = ({
  onSave,
  bookingPlatforms = [],
  type,
}: Props) => {
  const isIntegratedVendor = IntegrationsVendorTypes.includes(type);

  const [apiStatus, suppliers, fetchSuppliers] = useFetchData<
    BookingPlatformSupplier[]
  >();

  useEffect(() => {
    if (isIntegratedVendor) fetchSuppliers(() => Service.getSuppliers(type));
  }, [fetchSuppliers, isIntegratedVendor, type]);

  const selectedOptions = useMemo(() => {
    if (!suppliers) return [];

    const allSuppliersMap = new Map<string, string>();
    const selectedSuppliers: SelectOption[] = [];
    suppliers.forEach(s => allSuppliersMap.set(s.id, s.name));

    bookingPlatforms.forEach(p => {
      const supplierName = allSuppliersMap.get(p.supplierId);
      selectedSuppliers.push({
        value: p.supplierId,
        label: supplierName || p.supplierId,
      });
      allSuppliersMap.delete(p.supplierId);
    });

    return selectedSuppliers;
  }, [suppliers, bookingPlatforms]);

  const availableOptions = useMemo(() => {
    if (!suppliers) return [];

    const availableSuppliersMap = new Map<string, string>();
    suppliers.forEach(s => availableSuppliersMap.set(s.id, s.name));

    bookingPlatforms.forEach(p => availableSuppliersMap.delete(p.supplierId));

    return Array.from(availableSuppliersMap.entries()).map(
      ([value, label]) => ({
        value,
        label,
      })
    );
  }, [suppliers, bookingPlatforms]);

  const handleBookingPlatformChange = item => {
    onSave({ type: item.value, bookingPlatforms: [] });
  };
  const handleSuppliersChange = selectedOptions => {
    onSave({
      type,
      bookingPlatforms: selectedOptions.map(opt => ({
        type,
        supplierId: opt.value,
      })),
    });
  };

  return (
    <>
      <FieldView name={'Booking Platform'} vmd={3} vsm={4}>
        <Select
          options={VendorTypeOptions}
          value={mapVendorTypeToOption(type || VendorType.none)}
          onChange={handleBookingPlatformChange}
        />
      </FieldView>
      {isIntegratedVendor && (
        <FieldView name={'Company Identifier(s)'}>
          <Select
            isMulti
            options={availableOptions}
            value={selectedOptions}
            placeholder={'Loading suppliers...'}
            loadingMessage={() => 'Loading suppliers...'}
            isLoading={apiStatus !== ApiStatus.success}
            onChange={handleSuppliersChange}
          />
        </FieldView>
      )}
    </>
  );
};

export default BookingPlatformSelector;
